import { useParams, useNavigate } from 'react-router-dom'
import {
  useGetUserQuery,
  UserRole,
  useUpdateUserMutation,
  useResetPasswordMutation,
  useResetMfaMutation,
} from '../generated/graphql'
import { toast } from 'sonner'
import {
  LoadingPage,
  UserIcon,
  UserRoleBadge,
  UserSettingsPage,
  UserStatusBadge,
  type SettingsUserData,
} from '@valiant/ui-shared'
import { MainLayout } from '../components/layout/MainLayout'
import { useMemo } from 'react'
import { useAuth } from '../AuthProvider'
import { PasskeySettings } from '../components/PasskeySettings'

export function UserSettings() {
  const { userId } = useParams<{ userId: string }>()
  const { auth } = useAuth()

  const navigate = useNavigate()
  const [{ data: userData, fetching: userLoading, error: userError }] = useGetUserQuery({
    variables: { userId: userId! },
    pause: !userId,
  })
  const [{ fetching: updateLoading }, updateUser] = useUpdateUserMutation()
  const [{ fetching: resetPasswordLoading }, resetPassword] = useResetPasswordMutation()
  const [{ fetching: resetMfaLoading }, resetMfa] = useResetMfaMutation()

  const user = userData?.getUser

  const handleSave = async (formData: SettingsUserData) => {
    if (!user) return

    try {
      const result = await updateUser({
        input: {
          userId: user.id,
          username: formData.username !== user.username ? formData.username : undefined,
          email: formData.email !== user.email ? formData.email : undefined,
          role: formData.role !== user.role ? (formData.role as UserRole) : undefined,
          isActive: formData.isActive !== user.isActive ? formData.isActive : undefined,
          isWhitelisted:
            formData.isWhitelisted !== user.isWhitelisted ? formData.isWhitelisted : undefined,
          emailNotifications: formData.emailNotifications,
        },
      })

      if (result.error) {
        toast.error('Failed to update user: ' + result.error.message)
        throw new Error(result.error.message)
      } else {
        toast.success('User updated successfully')
        navigate(-1)
      }
    } catch (_error) {
      toast.error('Failed to update user')
      throw _error
    }
  }

  const handleResetPassword = async (userId: string) => {
    try {
      const result = await resetPassword({ userId })
      if (result.error) {
        toast.error('Failed to reset password: ' + result.error.message)
        throw new Error(result.error.message)
      } else {
        toast.success('Password reset successfully')
      }
    } catch (_error) {
      toast.error('Failed to reset password')
      throw _error
    }
  }

  const handleResetMfa = async (userId: string) => {
    try {
      const result = await resetMfa({ userId })
      if (result.error) {
        toast.error('Failed to reset MFA: ' + result.error.message)
        throw new Error(result.error.message)
      } else {
        toast.success('MFA reset successfully')
      }
    } catch (_error) {
      toast.error('Failed to reset MFA')
      throw _error
    }
  }

  const badges = useMemo(() => {
    if (!user) return []
    return [
      {
        node: <UserRoleBadge role={user.role} />,
        direction: 'LEFT',
      },
      {
        node: (
          <UserStatusBadge
            isWhitelisted={user.isWhitelisted}
            isTrader={user.role === UserRole.Trader}
            isActive={user.isActive}
          />
        ),
        direction: 'LEFT',
      },
    ]
  }, [user])

  if (userLoading) {
    return <LoadingPage />
  }

  if (userError || !user) {
    return (
      <MainLayout>
        <div>User not found</div>
      </MainLayout>
    )
  }

  const settingsUserData: SettingsUserData = {
    id: user.id,
    username: user.username,
    email: user.email,
    role: user.role,
    isActive: user.isActive,
    isWhitelisted: user.isWhitelisted,
    emailNotifications: user.emailNotifications || false,
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: user.username,
          icon: <UserIcon />,
          hideImage: true,
          badges: badges,
        },
      ]}
      title="User Settings"
    >
      <UserSettingsPage
        user={settingsUserData}
        loading={userLoading}
        showRoleSelector={false}
        showPermissionsButton={true}
        showPasswordReset={true}
        showMfaReset={true}
        showStatusToggles={true}
        showEmailNotifications={true}
        onSave={handleSave}
        onResetPassword={handleResetPassword}
        onResetMfa={handleResetMfa}
        saveLoading={updateLoading}
        passwordResetLoading={resetPasswordLoading}
        mfaResetLoading={resetMfaLoading}
      />
      {auth && <PasskeySettings token={auth.token} targetUserId={auth.userId === user.id ? undefined : user.id} />}
    </MainLayout>
  )
}
